<template>
    <b-modal v-model="showModal" title="Cargar Datos Bradley" size="md" @hidden="handleClose" no-close-on-backdrop>
        <b-overlay :show="uploading" rounded="sm">
            <div class="upload-container">
                <!-- Zona de arrastre de archivos -->
                <div class="drop-zone" :class="{ 'drag-over': isDragging }" @dragover.prevent="handleDragOver"
                    @dragleave.prevent="handleDragLeave" @drop.prevent="handleDrop" @click="selectFile">
                    <div class="drop-zone-content">
                        <i class="fa fa-upload fa-3x text-primary mb-3"></i>
                        <p class="mb-2"><strong>Arrastra el archivo aquí o haz clic para seleccionar</strong></p>
                        <p class="text-muted small">Solo archivos JSON (máx. 2MB)</p>
                    </div>
                </div>

                <!-- Vista previa del archivo seleccionado -->
                <div v-if="selectedFile" class="file-preview mt-3">
                    <div class="d-flex align-items-center justify-content-between p-3 bg-light rounded">
                        <div class="d-flex align-items-center">
                            <i class="fa fa-file-text-o fa-2x text-success mr-3"></i>
                            <div>
                                <div class="font-weight-bold">{{ selectedFile.name }}</div>
                                <small class="text-muted">{{ formatFileSize(selectedFile.size) }}</small>
                            </div>
                        </div>
                        <b-button size="sm" variant="outline-danger" @click="clearFile">
                            <i class="fa fa-trash"></i>
                        </b-button>
                    </div>
                </div>

                <!-- Vista previa del contenido JSON -->
                <div v-if="jsonPreview" class="json-preview mt-3">
                    <h6 class="mb-2">Vista previa:</h6>
                    <pre class="bg-light p-3 rounded">{{ jsonPreview }}</pre>
                </div>

                <!-- Propiedades faltantes -->
                <b-alert v-if="missingProperties.length > 0" variant="warning" show class="mt-3">
                    <i class="fa fa-exclamation-triangle"></i> <strong>Propiedades faltantes:</strong>
                    <ul class="mb-0 mt-2">
                        <li v-for="prop in missingProperties" :key="prop">{{ prop }}</li>
                    </ul>
                </b-alert>

                <!-- Mensaje de error -->
                <b-alert v-if="errorMessage" variant="danger" show dismissible @dismissed="errorMessage = null"
                    class="mt-3">
                    <i class="fa fa-exclamation-circle"></i> {{ errorMessage }}
                </b-alert>
            </div>
        </b-overlay>

        <template #modal-footer>
            <b-button variant="secondary" @click="handleClose" :disabled="uploading">
                Cancelar
            </b-button>
            <b-button variant="primary" @click="handleUpload"
                :disabled="!selectedFile || uploading || missingProperties.length > 0">
                <i class="fa fa-upload"></i> Cargar
            </b-button>
        </template>

        <!-- Input file oculto -->
        <input ref="fileInput" type="file" style="display: none" accept=".json,application/json"
            @change="handleFileSelected" />
    </b-modal>
</template>

<script>
import { mapActions } from 'vuex';

export default {
    name: 'BradleyUploadModal',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        diagnosticId: {
            type: Number,
            default: null
        },
        companyId: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            showModal: false,
            uploading: false,
            isDragging: false,
            selectedFile: null,
            jsonPreview: null,
            errorMessage: null,
            missingProperties: []
        };
    },
    watch: {
        visible(newVal) {
            this.showModal = newVal;
            if (!newVal) {
                this.clearFile();
            }
        },
        showModal(newVal) {
            if (!newVal) {
                this.$emit('update:visible', false);
            }
        }
    },
    methods: {
        ...mapActions('skudo/diagnostics', ['uploadBradleyData']),

        selectFile() {
            this.$refs.fileInput.click();
        },

        handleFileSelected(event) {
            const file = event.target.files[0];
            if (file) {
                this.processFile(file);
            }
            event.target.value = '';
        },

        handleDragOver(event) {
            event.preventDefault();
            this.isDragging = true;
        },

        handleDragLeave(event) {
            event.preventDefault();
            this.isDragging = false;
        },

        handleDrop(event) {
            event.preventDefault();
            this.isDragging = false;

            const files = event.dataTransfer.files;
            if (files.length > 0) {
                this.processFile(files[0]);
            }
        },

        async processFile(file) {
            this.errorMessage = null;
            this.missingProperties = [];

            if (!file.type.includes('json') && !file.name.endsWith('.json')) {
                this.errorMessage = 'El archivo debe ser de tipo JSON';
                return;
            }

            if (file.size > 2 * 1024 * 1024) {
                this.errorMessage = 'El archivo no debe superar los 2MB';
                return;
            }

            try {
                const content = await this.readFileContent(file);
                const jsonData = JSON.parse(content);

                const missing = this.validateBradleyStructure(jsonData);
                if (missing.length > 0) {
                    this.missingProperties = missing;
                    this.selectedFile = file;
                    var preview = JSON.stringify(jsonData, null, 2);
                    this.jsonPreview = preview.length > 2000 ? preview.substring(0, 2000) + '\n...' : preview;
                    return;
                }

                this.selectedFile = file;
                var preview = JSON.stringify(jsonData, null, 2);
                this.jsonPreview = preview.length > 2000 ? preview.substring(0, 2000) + '\n...' : preview;
            } catch (error) {
                this.errorMessage = 'El archivo no es un JSON válido';
            }
        },

        readFileContent(file) {
            return new Promise(function (resolve, reject) {
                const reader = new FileReader();
                reader.onload = function (e) {
                    resolve(e.target.result);
                };
                reader.onerror = function (e) {
                    reject(e);
                };
                reader.readAsText(file);
            });
        },

        validateBradleyStructure(data) {
            const missing = [];

            if (!data) {
                return ['Archivo JSON vacío'];
            }

            // Validar niveles_bradley
            if (!data.hasOwnProperty('niveles_bradley')) {
                missing.push('niveles_bradley');
            } else if (!Array.isArray(data.niveles_bradley) || data.niveles_bradley.length === 0) {
                missing.push('niveles_bradley (debe ser un array con elementos)');
            }

            // Validar curva_referencia
            if (!data.hasOwnProperty('curva_referencia')) {
                missing.push('curva_referencia');
            } else if (typeof data.curva_referencia !== 'object' || Array.isArray(data.curva_referencia)) {
                missing.push('curva_referencia (debe ser un objeto)');
            } else if (data.niveles_bradley && Array.isArray(data.niveles_bradley)) {
                // Validar que cada nivel tenga su valor en curva_referencia
                data.niveles_bradley.forEach(function (nivel) {
                    if (!data.curva_referencia.hasOwnProperty(nivel)) {
                        missing.push('curva_referencia.' + nivel);
                    }
                });
            }

            // Validar curva_planta
            if (!data.hasOwnProperty('curva_planta')) {
                missing.push('curva_planta');
            } else if (typeof data.curva_planta !== 'object' || Array.isArray(data.curva_planta)) {
                missing.push('curva_planta (debe ser un objeto)');
            } else if (data.niveles_bradley && Array.isArray(data.niveles_bradley)) {
                // Validar que cada nivel tenga su valor en curva_planta
                data.niveles_bradley.forEach(function (nivel) {
                    if (!data.curva_planta.hasOwnProperty(nivel)) {
                        missing.push('curva_planta.' + nivel);
                    }
                });
            }

            // Validar posicion_actual
            if (!data.hasOwnProperty('posicion_actual')) {
                missing.push('posicion_actual');
            } else if (typeof data.posicion_actual !== 'object' || Array.isArray(data.posicion_actual)) {
                missing.push('posicion_actual (debe ser un objeto)');
            } else {
                if (!data.posicion_actual.hasOwnProperty('nivel')) {
                    missing.push('posicion_actual.nivel');
                }
                if (!data.posicion_actual.hasOwnProperty('ratio_incidentes')) {
                    missing.push('posicion_actual.ratio_incidentes');
                }
            }

            // Validar mensaje_clave
            if (!data.hasOwnProperty('mensaje_clave')) {
                missing.push('mensaje_clave');
            } else if (typeof data.mensaje_clave !== 'string') {
                missing.push('mensaje_clave (debe ser un string)');
            }

            // Validar zonas_culturales
            if (!data.hasOwnProperty('zonas_culturales')) {
                missing.push('zonas_culturales');
            } else if (!Array.isArray(data.zonas_culturales)) {
                missing.push('zonas_culturales (debe ser un array)');
            } else if (data.zonas_culturales.length === 0) {
                missing.push('zonas_culturales (debe contener al menos un elemento)');
            } else {
                data.zonas_culturales.forEach(function (zona, index) {
                    // Validar nivel_desde (obligatorio)
                    if (!zona.hasOwnProperty('nivel_desde')) {
                        missing.push('zonas_culturales[' + index + '].nivel_desde');
                    }

                    // Validar etiqueta
                    if (!zona.hasOwnProperty('etiqueta')) {
                        missing.push('zonas_culturales[' + index + '].etiqueta');
                    } else {
                        if (!zona.etiqueta.hasOwnProperty('texto')) {
                            missing.push('zonas_culturales[' + index + '].etiqueta.texto');
                        }
                        if (!zona.etiqueta.hasOwnProperty('color_texto')) {
                            missing.push('zonas_culturales[' + index + '].etiqueta.color_texto');
                        }
                    }

                    // Validar posicion
                    if (!zona.hasOwnProperty('posicion')) {
                        missing.push('zonas_culturales[' + index + '].posicion');
                    } else {
                        if (!zona.posicion.hasOwnProperty('left')) {
                            missing.push('zonas_culturales[' + index + '].posicion.left');
                        }
                        if (!zona.posicion.hasOwnProperty('top')) {
                            missing.push('zonas_culturales[' + index + '].posicion.top');
                        }
                    }
                });
            }

            return missing;
        },

        async handleUpload() {
            if (!this.selectedFile) return;

            this.uploading = true;
            this.errorMessage = null;

            try {
                const content = await this.readFileContent(this.selectedFile);
                const jsonData = JSON.parse(content);

                const formData = new FormData();
                formData.append('bradley_data', JSON.stringify(jsonData));
                formData.append('diagnostic_id', this.diagnosticId);
                formData.append('company_id', this.companyId);

                const response = await this.uploadBradleyData(formData);

                if (response && response.data) {
                    this.$alertSuccess('Datos Bradley cargados exitosamente');
                    this.$emit('data-uploaded');
                    setTimeout(() => this.handleClose(), 100);
                } else {
                    this.errorMessage = response.message || 'Error al cargar los datos';
                }
            } catch (error) {
                this.errorMessage = (error.response && error.response.data && error.response.data.message) || 'Error al cargar los datos. Por favor, intente nuevamente.';
            } finally {
                this.uploading = false;
            }
        },

        clearFile() {
            this.selectedFile = null;
            this.jsonPreview = null;
            this.errorMessage = null;
            this.missingProperties = [];
        },

        handleClose() {
            this.clearFile();
            this.showModal = false;
            this.$emit('update:visible', false);
            this.$emit('close');
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
        }
    }
};
</script>

<style scoped>
.upload-container {
    min-height: 200px;
}

.drop-zone {
    border: 2px dashed #cbd5e0;
    border-radius: 12px;
    padding: 1rem 2rem;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    background-color: #f7fafc;
}

.drop-zone:hover {
    border-color: #3b82f6;
    background-color: #eff6ff;
}

.drop-zone.drag-over {
    border-color: #3b82f6;
    background-color: #dbeafe;
    transform: scale(1.02);
}

.drop-zone-content {
    pointer-events: none;
}

.file-preview {
    animation: fadeIn 0.3s ease;
}

.json-preview pre {
    font-size: 11px;
    margin: 0;
    max-height: 400px;
    overflow-y: auto;
    white-space: pre-wrap;
    word-break: break-word;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (max-width: 575px) {
    .drop-zone {
        padding: 2rem 1rem;
    }

    .drop-zone-content p {
        font-size: 14px;
    }
}
</style>
